import React, { useState, useEffect, useRef, useCallback } from 'react';
import { cleanSpeechTranscript } from '../utils/speechCleaner';
import { useNavigate } from 'react-router-dom';
import { NeuralOrb } from '../components/NeuralOrb';
import { signalAthenaAuthExpired, useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API_BASE } from '../config/api';
import { playAthenaAudio, requestAthenaTts } from '../voice/athenaTts';
import { useVoiceEngine } from '../hooks/useVoiceEngine';
import { isAthenaSessionFailure, normalizeAthenaApiFailure } from '../utils/athenaApiFailure';
import { registerAthenaQuickOverlayMessageHandler } from '../platform/AthenaQuickOverlayRuntime';

const API = API_BASE;

const IS_MOBILE = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  || (navigator.maxTouchPoints > 1 && window.innerWidth < 1024);

const VOICE_PASSIVE    = 'passive';
const VOICE_ACTIVE     = 'active';
const VOICE_PROCESSING = 'processing';
const VOICE_SPEAKING   = 'speaking';

function stripEmojis(t) {
  return t.replace(/[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2702}-\u{27B0}\u{24C2}-\u{1F251}\u{1f926}-\u{1f937}\u{10000}-\u{10ffff}\u{2600}-\u{2B55}\u{200d}\u{23cf}\u{23e9}\u{231a}\u{fe0f}\u{3030}]+/gu, '').trim();
}

// Injected with every visual call message — Visual Presence Mode directive
const VISUAL_CALL_INSTRUCTION =
  'MODO CÂMERA ATIVO — VISUAL PRESENCE: ' +
  'Você está observando continuamente o que o usuário está mostrando, como numa chamada de vídeo real. ' +
  'Responda como quem realmente viu a cena, usando linguagem de presença: ' +
  '"Vi que...", "Notei...", "Percebi...", "Enquanto você mostrava...", "Observei...". ' +
  'Prioridade absoluta: 1) O que está visível nos frames observados agora; 2) A pergunta do usuário; ' +
  '3) Memória pessoal SOMENTE se ajudar diretamente a resposta ao que foi visto. ' +
  'NUNCA mencione workspace, projetos ou conversas antigas sem o usuário pedir. ' +
  'NUNCA use frases genéricas como "Talvez...", "Imagino...", "Parece que..." — prefira o que foi realmente observado.';

// ── Voice level bars ──────────────────────────────────────────────────────────
// Deterministic oscillator — no AudioContext needed, no extra permissions
function VoiceLevelBars({ voiceState, side }) {
  const barsRef = useRef([]);
  const rafRef  = useRef(null);
  const N = 5;

  useEffect(() => {
    function tick() {
      const t = Date.now() * 0.001;
      barsRef.current.forEach((bar, i) => {
        if (!bar) return;
        const phase = i * 0.62 + (side === 'right' ? Math.PI : 0);
        let h, alpha;

        if (voiceState === VOICE_ACTIVE) {
          h     = 6 + Math.abs(Math.sin(t * 3.2 + phase) * 20 + Math.sin(t * 7.1 + phase * 1.3) * 9);
          alpha = 0.55 + Math.abs(Math.sin(t * 2.8 + phase)) * 0.45;
        } else if (voiceState === VOICE_SPEAKING) {
          h     = 5 + Math.abs(Math.sin(t * 4.5 + phase) * 16 + Math.sin(t * 2.1 + phase) * 7);
          alpha = 0.5  + Math.abs(Math.sin(t * 4.5 + phase)) * 0.5;
        } else if (voiceState === VOICE_PROCESSING) {
          h     = 4 + Math.abs(Math.sin(t * 1.4 + phase) * 11);
          alpha = 0.3  + Math.abs(Math.sin(t * 1.4 + phase)) * 0.25;
        } else {
          h     = 3 + Math.sin(t * 0.65 + phase) * 1.4;
          alpha = 0.14 + Math.sin(t * 0.5  + phase) * 0.04;
        }

        bar.style.height  = Math.max(3, h) + 'px';
        bar.style.opacity = Math.max(0.08, alpha);
      });
      rafRef.current = requestAnimationFrame(tick);
    }
    tick();
    return () => cancelAnimationFrame(rafRef.current);
  }, [voiceState, side]);

  const color = voiceState === VOICE_SPEAKING   ? '#34d399'
    : voiceState === VOICE_ACTIVE               ? '#00e5aa'
    : voiceState === VOICE_PROCESSING           ? '#fbbf24'
    : 'rgba(255,255,255,0.28)';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 3, height: 40 }}>
      {Array.from({ length: N }).map((_, i) => (
        <div key={i} ref={el => { barsRef.current[i] = el; }} style={{
          width: 3, borderRadius: 2, height: 3,
          background: color, opacity: 0.14,
          transition: 'background 0.55s ease',
        }} />
      ))}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function VisualCallPage() {
  const { user, invalidateSession }  = useAuth();
  const navigate  = useNavigate();
  const token     = localStorage.getItem('athena_token');

  // Camera
  const videoRef    = useRef(null);
  const streamRef   = useRef(null);
  const offscreenCanvas = useRef(document.createElement('canvas'));
  const [camFacing, setCamFacing] = useState('user');
  const [camReady,  setCamReady]  = useState(false);

  // Voice
  const continuousModeRef = useRef(true);
  const processingLockRef = useRef(false);
  const lastSentRef       = useRef({ text: '', ts: 0 });
  const speakingLockRef   = useRef(false);
  const speechQueueRef    = useRef([]);
  const currentAudioRef   = useRef(null);
  const ttsAbortRef       = useRef(null);
  const speechGenerationRef = useRef(0);
  const sendMessageRef = useRef(null);
  // Visual observation buffer — stores {ts, frame} pairs captured during voice input.
  // Privacy: cleared immediately after getObservationFrames() and on page unmount.
  const visualBufferRef        = useRef([]);
  const observationIntervalRef = useRef(null);

  // Chat
  const [sessionId, setSessionId] = useState(null);
  const [textInput, setTextInput] = useState('');
  const [toast,     setToast]     = useState('');

  // ── Toast ───────────────────────────────────────────────────────────────────
  const showToast = useCallback((msg, dur = 3500) => {
    setToast(msg);
    setTimeout(() => setToast(''), dur);
  }, []);

  // Visual Call has its own camera UI, but deliberately shares the exact same
  // voice facade, turn buffer and Android provider as Home and Workspace.
  const {
    voiceState,
    continuousMode,
    startActive,
    startPassive,
    startManual,
    finishManual,
    startSpeaking,
    doneSpeaking,
    finishResponseWithoutSpeech,
    cancelSpeaking,
    stop: engineStop,
    setProcessing,
    setIdle,
    setContinuousMode,
    beginBargeIn,
  } = useVoiceEngine({
    context: 'visual-call',
    initialContinuousMode: true,
    onDispatch: (rawText) => {
      const { cleanTranscript, isCorrupted, confidence } = cleanSpeechTranscript(rawText);
      if (!cleanTranscript || cleanTranscript.length <= 2 || (isCorrupted && confidence < 0.35)) {
        showToast('Não consegui entender essa fala com segurança. Tente novamente ou envie por texto.');
        setIdle();
        return;
      }
      sendMessageRef.current?.(cleanTranscript);
    },
    onCaptureFailure: (failure) => {
      const denied = ['not-allowed', 'service-not-allowed'].includes(failure?.reason);
      const recognizerPermissionError = failure?.reason === 'recognizer_permission_error';
      showToast(denied
        ? 'A Athena não recebeu acesso ao microfone. Libere a permissão e tente novamente.'
        : recognizerPermissionError
          ? 'A permissão está ativa, mas o serviço de voz do Android não conseguiu acessar o áudio. Verifique o microfone global e o reconhecimento de voz do aparelho.'
        : 'Não consegui transcrever sua fala. Verifique a conexão e tente novamente.');
    },
  });

  useEffect(() => { continuousModeRef.current = continuousMode; }, [continuousMode]);

  // ── Camera ──────────────────────────────────────────────────────────────────
  const startCamera = useCallback(async (facing) => {
    setCamReady(false);

    // Stop old tracks and detach from element before requesting new stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;

    if (!navigator.mediaDevices?.getUserMedia) {
      showToast('Câmera não disponível neste dispositivo.');
      return;
    }

    const target = facing || 'user';
    // Try exact facing mode first, then relaxed, then any video as last resort
    const attempts = [
      { video: { facingMode: { exact: target }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false },
      { video: { facingMode: target,            width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false },
      { video: true, audio: false },
    ];

    let stream = null;
    let lastErr = null;
    for (const c of attempts) {
      try { stream = await navigator.mediaDevices.getUserMedia(c); break; }
      catch (e) { lastErr = e; }
    }

    if (!stream) {
      const n   = lastErr?.name || '';
      const back = target === 'environment';
      const msg = (n === 'NotFoundError' || n === 'DevicesNotFoundError')
        ? (back ? 'Câmera traseira não encontrada neste dispositivo.' : 'Câmera não encontrada.')
        : (n === 'NotAllowedError' || n === 'PermissionDeniedError')
        ? 'Permissão de câmera negada. Verifique as configurações do navegador.'
        : 'Não foi possível acessar a câmera. Tente novamente.';
      showToast(msg);
      return;
    }

    streamRef.current = stream;
    if (videoRef.current) {
      videoRef.current.srcObject = stream;
      try { await videoRef.current.play(); } catch {}
    }
    setCamReady(true);
  }, [showToast]);

  // Re-attach stream whenever video element remounts
  useEffect(() => {
    if (videoRef.current && streamRef.current && !videoRef.current.srcObject) {
      videoRef.current.srcObject = streamRef.current;
    }
  });

  // Mount: start camera. Unmount: stop stream + voice + tts.
  useEffect(() => {
    startCamera('user');
    return () => {
      streamRef.current?.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const flipCamera = useCallback(async () => {
    const next = camFacing === 'user' ? 'environment' : 'user';
    setCamFacing(next);
    await startCamera(next);
  }, [camFacing, startCamera]);

  // ── Frame capture (silent — video element never touched) ────────────────────
  // Draws current video frame to an invisible offscreen canvas.
  // The <video> element stays playing and visible at all times — no flicker.
  const captureFrame = useCallback(() => {
    const vid = videoRef.current;
    if (!vid || !streamRef.current || !camReady) return null;
    const canvas = offscreenCanvas.current;
    canvas.width  = vid.videoWidth  || 1280;
    canvas.height = vid.videoHeight || 720;
    const ctx = canvas.getContext('2d');
    ctx.save();
    if (camFacing === 'user') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(vid, 0, 0);
    ctx.restore();
    const dataUrl = canvas.toDataURL('image/jpeg', 0.82);
    return dataUrl.split(',')[1]; // base64 only
  }, [camFacing, camReady]);

  // ── Observation Window — multi-frame capture during voice input ──────────────
  // Privacy: frames stored only in-memory (useRef), never persisted.
  // Buffer cleared immediately after getObservationFrames() or on unmount.
  const startObservation = useCallback(() => {
    visualBufferRef.current = [];
    const f0 = captureFrame();
    if (f0) visualBufferRef.current.push({ ts: Date.now(), frame: f0 });

    if (observationIntervalRef.current) clearInterval(observationIntervalRef.current);
    observationIntervalRef.current = setInterval(() => {
      const f = captureFrame();
      if (f) {
        visualBufferRef.current.push({ ts: Date.now(), frame: f });
        if (visualBufferRef.current.length > 4) visualBufferRef.current.shift();
      }
    }, 3000); // capture every 3 seconds
  }, [captureFrame]);

  const getObservationFrames = useCallback(() => {
    // Only return frames captured within the last 10 seconds (current voice session)
    const now    = Date.now();
    const frames = visualBufferRef.current
      .filter(item => now - item.ts < 10000)
      .slice(-3)
      .map(item => item.frame)
      .filter(Boolean);
    // Discard immediately — privacy
    visualBufferRef.current = [];
    if (observationIntervalRef.current) {
      clearInterval(observationIntervalRef.current);
      observationIntervalRef.current = null;
    }
    return frames;
  }, []);

  // ── Speech helpers ───────────────────────────────────────────────────────────
  const stopSpeech = useCallback(({ preserveEngineState = false } = {}) => {
    speechGenerationRef.current += 1;
    ttsAbortRef.current?.abort();
    ttsAbortRef.current = null;
    if (currentAudioRef.current) {
      try { currentAudioRef.current.pause(); currentAudioRef.current.removeAttribute('src'); currentAudioRef.current.load(); } catch {}
      currentAudioRef.current = null;
    }
    speakingLockRef.current = false;
    speechQueueRef.current  = [];
    // No barge-in o motor ainda precisa enxergar SPEAKING para fazer a
    // transição atômica SPEAKING → IDLE → LISTENING. Cancelá-lo antes faria
    // beginBargeIn() recusar a abertura do microfone.
    if (!preserveEngineState) cancelSpeaking();
  }, [cancelSpeaking]);

  const stopAllListening = useCallback(() => {
    // Stop observation interval — buffer preserved so sendMessage can still read it
    if (observationIntervalRef.current) { clearInterval(observationIntervalRef.current); observationIntervalRef.current = null; }
    engineStop();
  }, [engineStop]);

  // ── TTS (exact same pattern as AthenaPage) ──────────────────────────────────
  const speakText = useCallback(async (text) => {
    const clean = stripEmojis(text);
    if (!clean) {
      finishResponseWithoutSpeech();
      return;
    }

    if (speakingLockRef.current) {
      speechQueueRef.current.push(clean);
      return;
    }
    speakingLockRef.current = true;
    let nextText = clean;
    let playbackStarted = false;
    let unavailable = false;
    while (nextText) {
      const generation = ++speechGenerationRef.current;
      const controller = new AbortController();
      ttsAbortRef.current = controller;
      const result = await requestAthenaTts({ api: API, token, text: nextText, signal: controller.signal });
      if (speechGenerationRef.current !== generation) return;
      if (result?.audio) {
        const playback = await playAthenaAudio(result.audio, {
          signal: controller.signal,
          onCreated: (audio) => { currentAudioRef.current = audio; },
          onStart: () => {
            playbackStarted = true;
            startSpeaking();
          },
        });
        if (playback.status !== 'finished' && playback.status !== 'cancelled') unavailable = true;
      } else {
        unavailable = true;
      }
      if (speechGenerationRef.current !== generation) return;
      currentAudioRef.current = null;
      ttsAbortRef.current = null;
      nextText = speechQueueRef.current.shift() || null;
    }

    speakingLockRef.current = false;
    if (unavailable) showToast('A resposta foi exibida, mas a voz da Athena está indisponível agora.');
    if (playbackStarted) doneSpeaking();
    else finishResponseWithoutSpeech();
  }, [token, startSpeaking, doneSpeaking, finishResponseWithoutSpeech, showToast]);

  // ── sendMessage — captures frame silently and sends with text ───────────────
  const sendMessage = useCallback(async (text, { fromQuickOverlay = false } = {}) => {
    if (!text.trim()) return { accepted: false, reason: 'empty' };
    if (processingLockRef.current) return { accepted: false, reason: 'processing' };
    const nowTs = Date.now();
    if (lastSentRef.current.text === text.trim() && nowTs - lastSentRef.current.ts < 2500) {
      return { accepted: false, reason: 'duplicate' };
    }
    lastSentRef.current = { text: text.trim(), ts: nowTs };
    processingLockRef.current = true;

    stopSpeech();
    setProcessing();

    // Collect observation window frames (captured during voice input)
    // A floating text card is never a camera-consent shortcut. It reuses this
    // authenticated chat route without reading the visual-call frame buffer.
    const obsFrames = fromQuickOverlay ? [] : getObservationFrames();
    // Always append the current frame as the freshest snapshot
    const currentFrame = fromQuickOverlay ? null : captureFrame();
    if (currentFrame && !obsFrames.includes(currentFrame)) obsFrames.push(currentFrame);

    // Up to 3 frames sent as attachments — backend analyzes each for sequential context
    const attachments = obsFrames.slice(-3).map((f, i, arr) => ({
      type: 'image/jpeg',
      data: f,
      name: i === arr.length - 1 ? 'camera.jpg' : `camera_obs_${i + 1}.jpg`,
    }));

    try {
      const payload = {
        message: text,
        session_id: sessionId,
        social_prefs: {},
        ...(fromQuickOverlay ? {} : {
          visual_call: true,
          cognitive_directive: VISUAL_CALL_INSTRUCTION,
        }),
        ...(attachments.length > 0 ? { attachments } : {}),
      };
      const { data } = await axios.post(`${API}/chat/message`, payload, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
        timeout: 30_000,
      });
      if (data?.is_error || !String(data?.response || '').trim()) {
        const responseError = new Error(data?.error_code || 'ATHENA_EMPTY_RESPONSE');
        responseError.response = { status: data?.is_error ? 503 : 502, data };
        throw responseError;
      }
      const response = (data.response || '').replace(/<action>[\s\S]*?<\/action>/g, '').trim();
      if (data.session_id) setSessionId(data.session_id);
      processingLockRef.current = false;
      if (fromQuickOverlay) finishResponseWithoutSpeech();
      else await speakText(response);
      return { accepted: true, response };
    } catch (error) {
      processingLockRef.current = false;
      const failure = normalizeAthenaApiFailure(error);
      if (isAthenaSessionFailure(failure)) {
        signalAthenaAuthExpired({ token });
        await invalidateSession?.(token);
        navigate('/login', { replace: true });
        return {
          accepted: false,
          reason: 'session_expired',
          response: 'Sua sessão expirou. Abra a Athena para entrar novamente.',
        };
      }
      showToast(failure.userMessage);
      finishResponseWithoutSpeech();
      return { accepted: false, reason: failure.code || 'request_failed', response: failure.userMessage };
    }
  }, [sessionId, token, captureFrame, getObservationFrames, speakText, stopSpeech, setProcessing, finishResponseWithoutSpeech, invalidateSession, navigate, showToast]);

  useEffect(() => {
    sendMessageRef.current = sendMessage;
    return () => { sendMessageRef.current = null; };
  }, [sendMessage]);

  useEffect(() => registerAthenaQuickOverlayMessageHandler(async ({ text }) => {
    const send = sendMessageRef.current;
    if (typeof send !== 'function') {
      return { accepted: false, response: 'Abra a Athena e tente novamente. A conversa ainda não está pronta.' };
    }
    return send(text, { fromQuickOverlay: true });
  }), []);

  // ── Voice gestures ──────────────────────────────────────────────────────────
  const handleTouchActivate = useCallback((action = 'hold-start') => {
    if (action === 'hold-end' && !continuousModeRef.current) {
      finishManual();
      return;
    }
    if (action !== 'hold-start') return;

    const wasSpeaking = voiceState === VOICE_SPEAKING;
    stopSpeech({ preserveEngineState: wasSpeaking });
    startObservation();

    if (wasSpeaking) {
      beginBargeIn();
      return;
    }
    if (continuousModeRef.current) startActive();
    else startManual();
  }, [voiceState, stopSpeech, startObservation, startActive, startManual, finishManual, beginBargeIn]);

  // ── On mount: start listening — fresh session, sem contexto antigo de workspace ──
  useEffect(() => {
    const t = IS_MOBILE ? null : setTimeout(() => startPassive(), 1500);
    return () => {
      if (t) clearTimeout(t);
      stopAllListening();
      stopSpeech();
      // Privacy: discard any remaining visual frames on page close
      visualBufferRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startPassive, stopAllListening, stopSpeech]);

  // ── Text submit ─────────────────────────────────────────────────────────────
  const handleTextSubmit = (e) => {
    e.preventDefault();
    if (textInput.trim()) {
      stopAllListening();
      sendMessage(textInput.trim());
      setTextInput('');
    }
  };

  // ── Close — stops camera, voice, TTS, navigates back ────────────────────────
  const handleClose = useCallback(() => {
    stopSpeech();
    stopAllListening();
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    navigate(-1);
  }, [navigate, stopSpeech, stopAllListening]);

  // ── Derived orb props ────────────────────────────────────────────────────────
  const orbListening  = voiceState === VOICE_ACTIVE;
  const orbSpeaking   = voiceState === VOICE_SPEAKING;
  const orbProcessing = voiceState === VOICE_PROCESSING;
  const mirrorStyle   = camFacing === 'user' ? 'scaleX(-1)' : 'none';

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#000', overflow: 'hidden' }}>

      {/* ── Keyframes ─────────────────────────────────────────────────────────── */}
      <style>{`@keyframes vcSpin { to { transform: rotate(360deg); } }`}</style>

      {/* ── Camera layer — continuous MediaStream, never flickers ─────────────── */}
      <video
        ref={videoRef}
        autoPlay muted playsInline
        style={{
          position: 'absolute', inset: 0,
          width: '100%', height: '100%',
          objectFit: 'cover',
          transform: mirrorStyle,
          opacity: camReady ? 1 : 0,
          transition: 'opacity 0.6s ease',
        }}
      />

      {/* Camera loading placeholder */}
      {!camReady && (
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(180deg, #030308 0%, #0a0f2a 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column', gap: 14,
        }}>
          <div style={{
            width: 26, height: 26, borderRadius: '50%',
            border: '2px solid rgba(0,200,212,0.18)', borderTopColor: '#00c8d4',
            animation: 'vcSpin 0.8s linear infinite',
          }} />
          <span style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.28)', letterSpacing: '.04em' }}>
            Acessando câmera…
          </span>
        </div>
      )}

      {/* ── Gradient overlay — makes UI readable over any camera background ───── */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: [
          'linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.1) 30%)',
          'linear-gradient(to top,   rgba(0,0,0,0.80) 0%, rgba(0,0,0,0.0) 55%)',
        ].join(', '),
      }} />

      {/* ── Top bar ───────────────────────────────────────────────────────────── */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '20px 20px 0', zIndex: 20,
      }}>
        {/* Athena Online pill — smaller, more discreet */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'rgba(0,0,0,0.40)', backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 20, padding: '5px 10px',
        }}>
          <div style={{
            width: 6, height: 6, borderRadius: '50%',
            background: '#22c55e', boxShadow: '0 0 7px #22c55e',
          }} />
          <div>
            <div style={{
              fontSize: 11, fontWeight: 600,
              color: 'rgba(255,255,255,0.72)', lineHeight: 1.2,
            }}>Athena Online</div>
            <div style={{
              fontSize: 8.5, color: 'rgba(255,255,255,0.32)',
              letterSpacing: '.03em',
            }}>Conexão Segura</div>
          </div>
        </div>

        {/* Close */}
        <button
          onClick={handleClose}
          title="Encerrar chamada"
          style={{
            width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
            background: 'rgba(239,68,68,0.18)', border: '1px solid rgba(239,68,68,0.38)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: '#ef4444', fontSize: 20, lineHeight: 1,
            backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
          }}>×</button>
      </div>

      {/* ── Flip camera button — circular, icon only, top-right below close ──── */}
      {camReady && (
        <button
          onClick={flipCamera}
          title="Virar câmera"
          style={{
            position: 'absolute', top: 70, right: 20, zIndex: 20,
            width: 38, height: 38, borderRadius: '50%',
            background: 'rgba(0,0,0,0.50)', border: '1px solid rgba(255,255,255,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: 'rgba(255,255,255,0.70)',
            backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
            fontSize: 18, lineHeight: 1,
          }}>↻</button>
      )}

      {/* ── Orb + lateral indicators ──────────────────────────────────────────── */}
      <div style={{
        position: 'absolute', left: 0, right: 0,
        bottom: 90,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: 12, zIndex: 20,
        pointerEvents: 'none',
      }}>
        {/* Left bars — scaled proportionally to smaller orb */}
        <div style={{ pointerEvents: 'none', transform: 'scale(0.65)', transformOrigin: 'center' }}>
          <VoiceLevelBars voiceState={voiceState} side="left" />
        </div>

        {/* NeuralOrb — scaled to ~110px effective (canvas 320px × 0.34) */}
        <div style={{
          width: 110, height: 110, flexShrink: 0,
          position: 'relative', pointerEvents: 'auto',
        }}>
          <div style={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%) scale(0.34)',
            pointerEvents: 'auto',
          }}>
            <NeuralOrb
              isListening={orbListening}
              isSpeaking={orbSpeaking}
              isProcessing={orbProcessing}
              onTouchActivate={handleTouchActivate}
              continuousMode={continuousMode}
            />
          </div>
        </div>

        {/* Right bars — scaled proportionally to smaller orb */}
        <div style={{ pointerEvents: 'none', transform: 'scale(0.65)', transformOrigin: 'center' }}>
          <VoiceLevelBars voiceState={voiceState} side="right" />
        </div>
      </div>

      {/* ── Bottom input bar — clean: only text field + send ──────────────────── */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        padding: '10px 16px 28px', zIndex: 20,
      }}>
        <form
          onSubmit={handleTextSubmit}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: 'rgba(6,10,24,0.72)', backdropFilter: 'blur(22px)',
            WebkitBackdropFilter: 'blur(22px)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 22, padding: '11px 18px',
            maxWidth: 520, margin: '0 auto',
          }}
        >
          <input
            value={textInput}
            onChange={e => setTextInput(e.target.value)}
            placeholder={
              voiceState === VOICE_ACTIVE     ? 'Ouvindo…'
              : voiceState === VOICE_PROCESSING ? 'Processando…'
              : voiceState === VOICE_SPEAKING   ? 'Respondendo…'
              : IS_MOBILE ? 'Segure o orbe ou digite…'
              : 'Diga "Athena" ou digite…'
            }
            style={{
              flex: 1, background: 'none', border: 'none', outline: 'none',
              color: 'rgba(255,255,255,0.85)', fontSize: 14, fontFamily: 'inherit',
            }}
          />
          {textInput.trim() && (
            <button
              type="submit"
              style={{
                width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                background: 'rgba(0,200,212,0.15)', border: '1px solid rgba(0,200,212,0.3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', color: '#00c8d4',
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z"/>
              </svg>
            </button>
          )}
        </form>
      </div>

      {/* ── Toast ─────────────────────────────────────────────────────────────── */}
      {toast && (
        <div style={{
          position: 'absolute', bottom: 96, left: '50%',
          transform: 'translateX(-50%)', zIndex: 30,
          background: 'rgba(8,12,28,0.92)', backdropFilter: 'blur(10px)',
          border: '1px solid rgba(0,200,212,0.25)',
          borderRadius: 12, padding: '10px 22px',
          fontSize: 13, color: 'rgba(255,255,255,0.78)',
          whiteSpace: 'nowrap', maxWidth: '88vw', textAlign: 'center',
        }}>
          {toast}
        </div>
      )}
    </div>
  );
}
